import {
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  useMediaQuery,
  Theme,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@material-ui/icons/Visibility';
import * as React from 'react';
import {
  DelayedLinearProgress,
  SortableTable,
  ConfirmDialog,
} from 'components';
import { SortableTableHeader } from 'components/SortableTable/components';
import { ReservationService } from 'services';
import { Reservation } from 'interfaces';
import { EditReservationDialog } from '../EditReservationDialog';
import { ApproveReservationDialog } from '../ApproveReservationDialog';

interface Props {
  refreshKey: number;
  onChanged: () => void;
}

const columns: SortableTableHeader[] = [
  { key: 'reserved_at', label: 'Date / Time' },
  { key: 'name', label: 'Name' },
  { key: 'party_size', label: 'Party' },
  { key: 'experience', label: 'Experience' },
  { key: 'created', label: 'Submitted' },
  { key: 'contact', label: 'Contact' },
  { key: 'controls', sortable: false },
];

export const RequestsTab: React.FC<Props> = ({ refreshKey, onChanged }) => {
  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = React.useState<Reservation[]>([]);
  const [editing, setEditing] = React.useState<Reservation | null>(null);
  const [declining, setDeclining] = React.useState<Reservation | null>(null);
  const [approving, setApproving] = React.useState<Reservation | null>(null);
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));

  const fetchRows = React.useCallback(() => {
    setLoading(true);
    ReservationService.getAll({ status: 'requested' })
      .then((data) => setRows(data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    fetchRows();
  }, [fetchRows, refreshKey]);

  // Approval no longer flips status directly. The Approve button opens
  // ApproveReservationDialog where the admin assigns a table; both the
  // table and the status change get sent in a single update.

  const decline = async (r: Reservation) => {
    try {
      await ReservationService.update(r.id, { status: 'declined' });
      setDeclining(null);
      fetchRows();
      onChanged();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert((e as Error)?.message || 'Failed to decline');
    }
  };

  const tableRows = rows.map((r) => {
    const dt = new Date(r.reservedAt);
    const created = new Date(r.created as any);
    return {
      key: String(r.id),
      cells: [
        {
          key: 'reserved_at',
          display: (
            <span>
              <strong>{dt.toLocaleDateString()}</strong>
              <br />
              {dt.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          ),
        },
        { key: 'name', display: r.name },
        { key: 'party_size', display: r.partySize },
        { key: 'experience', display: r.experience?.title || '—' },
        {
          key: 'created',
          display: isNaN(created.getTime())
            ? '—'
            : created.toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              }),
        },
        {
          key: 'contact',
          display: (
            <span style={{ fontSize: '0.85rem' }}>
              {r.email}
              <br />
              {r.phoneNumber}
            </span>
          ),
        },
        {
          key: 'controls',
          display: (
            <div style={{ display: 'flex', gap: 4 }}>
              <IconButton
                size="small"
                title="View / edit details"
                onClick={() => setEditing(r)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
              <Button
                size="small"
                variant="contained"
                style={{ background: '#43a047', color: 'white', minWidth: 72 }}
                startIcon={<CheckIcon />}
                onClick={() => setApproving(r)}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={() => setDeclining(r)}
              >
                Decline
              </Button>
            </div>
          ),
        },
      ],
    };
  });

  return (
    <React.Fragment>
      <Paper style={{ padding: 12, marginBottom: 8 }}>
        <Typography variant="subtitle2">
          Pending reservation requests — review and approve or decline.
          Approving will (Phase B) send a confirmation email to the customer.
          {rows.length > 0 && (
            <Chip
              label={`${rows.length} pending`}
              size="small"
              style={{ marginLeft: 8 }}
            />
          )}
        </Typography>
      </Paper>

      {loading && <DelayedLinearProgress />}

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.length === 0 && !loading && (
            <Paper style={{ padding: 24, textAlign: 'center', color: '#888' }}>
              No pending requests.
            </Paper>
          )}
          {rows.map((r) => {
            const dt = new Date(r.reservedAt);
            const created = new Date(r.created as any);
            return (
              <Paper
                key={r.id}
                style={{
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <span
                    style={{ fontSize: 17, fontWeight: 700, lineHeight: 1.2 }}
                  >
                    {dt.toLocaleDateString([], {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    })}
                    {' · '}
                    {dt.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <span style={{ fontSize: 11, color: '#888' }}>
                    {isNaN(created.getTime())
                      ? ''
                      : `submitted ${created.toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`}
                  </span>
                </div>
                <div
                  style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}
                >
                  {r.name}
                  <span
                    style={{
                      fontWeight: 400,
                      marginLeft: 8,
                      color: '#666',
                    }}
                  >
                    · party {r.partySize}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    fontSize: 13,
                    color: '#555',
                  }}
                >
                  {r.email && <span>{r.email}</span>}
                  {r.phoneNumber && <span>{r.phoneNumber}</span>}
                </div>
                {r.experience && (
                  <div style={{ fontSize: 13, color: '#1D8B6E' }}>
                    Experience: <strong>{r.experience.title}</strong>
                  </div>
                )}
                {r.notes && (
                  <div style={{ fontSize: 13, color: '#444', fontStyle: 'italic' }}>
                    "{r.notes}"
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    marginTop: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  <Button
                    size="small"
                    variant="contained"
                    style={{
                      background: '#43a047',
                      color: 'white',
                      flex: 1,
                    }}
                    startIcon={<CheckIcon />}
                    onClick={() => setApproving(r)}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    startIcon={<CloseIcon />}
                    onClick={() => setDeclining(r)}
                    style={{ flex: 1 }}
                  >
                    Decline
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<VisibilityIcon />}
                    onClick={() => setEditing(r)}
                    style={{ flex: 1 }}
                  >
                    Details
                  </Button>
                </div>
              </Paper>
            );
          })}
        </div>
      ) : (
        <Paper style={{ overflow: 'auto' }}>
          <SortableTable
            columns={columns}
            rows={tableRows}
            emptyTableText="No pending requests."
            disablePagination
          />
        </Paper>
      )}

      <EditReservationDialog
        open={!!editing}
        reservation={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          fetchRows();
          onChanged();
        }}
      />

      <ConfirmDialog
        title={`Decline reservation request from ${declining?.name || ''}?`}
        dialogProps={{
          open: !!declining,
          onClose: () => setDeclining(null),
        }}
        onConfirm={() => {
          if (declining) decline(declining);
        }}
      />

      <ApproveReservationDialog
        open={!!approving}
        reservation={approving}
        onClose={() => setApproving(null)}
        onApproved={() => {
          setApproving(null);
          fetchRows();
          onChanged();
        }}
      />
    </React.Fragment>
  );
};
